import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Star, GraduationCap, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

interface WelcomeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isAuthenticated: boolean;
  onOptionSelect?: (type: 'trainer' | 'investor' | 'early-bird') => void;
}

export const WelcomeDialog = ({ open, onOpenChange, isAuthenticated, onOptionSelect }: WelcomeDialogProps) => {
  const handleOptionClick = (type: 'trainer' | 'investor' | 'early-bird') => {
    if (onOptionSelect) {
      onOptionSelect(type);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gradient-to-br from-white to-pink-50 border-2 border-pink-200 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
            Welcome to HOTBOT! 🎉
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <p className="text-lg text-gray-700 text-center">
            HOTBOT is your ultimate AI companion for managing client communications.
            We're revolutionizing the way professionals interact with their clients
            through intelligent automation and personalized responses.
          </p>

          {!isAuthenticated ? (
            <div className="space-y-4">
              <p className="text-sm text-center text-gray-600">
                Sign up or log in to get started with HOTBOT!
              </p>
              <div className="flex justify-center gap-4">
                <Button asChild variant="default" className="bg-hotbot-pink hover:bg-hotbot-purple">
                  <Link to="/signup">Sign Up</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/login">Log In</Link>
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                className="p-4 rounded-xl bg-gradient-to-br from-pink-100 to-purple-100 hover:from-pink-200 hover:to-purple-200 
                         border-2 border-transparent hover:border-hotbot-pink transition-all duration-300"
                onClick={() => handleOptionClick('trainer')}
              >
                <GraduationCap className="w-8 h-8 mx-auto mb-2 text-hotbot-pink" />
                <h3 className="font-bold text-gray-800">Bot Trainer</h3>
                <p className="text-sm text-gray-600">Shape AI communication</p>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                className="p-4 rounded-xl bg-gradient-to-br from-purple-100 to-indigo-100 hover:from-purple-200 hover:to-indigo-200 
                         border-2 border-transparent hover:border-hotbot-purple transition-all duration-300"
                onClick={() => handleOptionClick('investor')}
              >
                <Crown className="w-8 h-8 mx-auto mb-2 text-hotbot-purple" />
                <h3 className="font-bold text-gray-800">Investor</h3>
                <p className="text-sm text-gray-600">Join our growth journey</p>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                className="p-4 rounded-xl bg-gradient-to-br from-hotbot-gold/20 to-hotbot-yellow/20 hover:from-hotbot-gold/30 hover:to-hotbot-yellow/30 
                         border-2 border-transparent hover:border-hotbot-gold transition-all duration-300"
                onClick={() => handleOptionClick('early-bird')}
              >
                <Star className="w-8 h-8 mx-auto mb-2 text-hotbot-gold" />
                <h3 className="font-bold text-gray-800">Early Bird</h3>
                <p className="text-sm text-gray-600">Special launch access</p>
              </motion.button>
            </div>
          )}

          <div className="mt-4 p-4 bg-gradient-to-r from-hotbot-gold/10 to-hotbot-yellow/10 rounded-xl border border-hotbot-gold/20">
            <h3 className="text-lg font-bold text-hotbot-gold mb-2">🌟 Limited Time Offer!</h3>
            <p className="text-sm text-gray-600">
              Secure your early bird spot for just $10 and get guaranteed access
              when we launch, plus exclusive perks and benefits!
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};